import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setSuccess(true);
      toast.success('Senha redefinida com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <p className="text-slate-600 mb-4">Link de redefinicao invalido.</p>
            <Link to="/login" className="text-[#C5A059] font-semibold hover:underline">Voltar ao Login</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Senha Redefinida!</h2>
            <p className="text-slate-500 text-sm mb-6">Sua senha foi alterada com sucesso. Faca login com sua nova senha.</p>
            <Link to="/login" data-testid="back-to-login-link">
              <Button className="bg-[#0A2647] hover:bg-[#0A2647]/90 text-white font-semibold w-full h-11">
                Ir para o Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A2647] flex items-center justify-center p-4">
      <Card className="max-w-md w-full border-0 shadow-2xl">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <Link to="/">
              <span className="text-[#C5A059] font-bold text-lg" style={{ fontFamily: 'Montserrat, sans-serif' }}>Falo Com O Dono</span>
            </Link>
            <h2 className="text-xl font-bold text-slate-900 mt-3">Nova Senha</h2>
            <p className="text-sm text-slate-500">Digite sua nova senha abaixo</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Nova Senha</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 pr-10"
                placeholder="Mínimo 6 caracteres"
                data-testid="reset-password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-[34px] text-slate-400">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Confirmar Senha</label>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-11"
                placeholder="Repita a senha"
                data-testid="reset-confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C5A059] hover:bg-[#C5A059]/90 text-[#0A2647] font-bold h-11"
              data-testid="reset-password-submit"
            >
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Redefinindo...</> : 'Redefinir Senha'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
